import { useState, useCallback } from 'react';
import { Loader2, Play, AlertTriangle } from 'lucide-react';
import { api } from '@halo/web-lib';
import { InfoBadge } from '../../../components/ui/Tooltip';
import type { ActionDefinition, ActionExecution } from '../types';
import { DANGER_STYLES, PLATFORM_OPTIONS, QUEUE_OPTIONS } from '../types';

interface StoreOption {
  id: number;
  name: string;
}

interface ActionCardProps {
  action: ActionDefinition;
  stores: StoreOption[];
  onExecuted: (execution: ActionExecution) => void;
  onToast: (msg: string, type: 'success' | 'error') => void;
}

export function ActionCard({ action, stores, onExecuted, onToast }: ActionCardProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [executing, setExecuting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    data?: Record<string, unknown>;
    error?: string;
  } | null>(null);

  const Icon = action.icon;
  const danger = DANGER_STYLES[action.dangerLevel];

  const setValue = useCallback((name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const isValid = action.fields.filter((f) => f.required).every((f) => values[f.name]?.trim());

  const execute = useCallback(async () => {
    setExecuting(true);
    setResult(null);
    setConfirming(false);
    const startTime = Date.now();

    try {
      const payload = action.buildPayload ? action.buildPayload(values) : {};
      const res = await api.post<Record<string, unknown>>(action.endpoint, payload);
      const durationMs = Date.now() - startTime;

      if (res.success) {
        setResult({ success: true, data: res.data });
        onToast(`${action.name} completed`, 'success');
        onExecuted({
          id: crypto.randomUUID(),
          actionId: action.id,
          actionName: action.name,
          timestamp: new Date().toISOString(),
          inputs: { ...values },
          status: 'success',
          result: res.data,
          durationMs,
        });
      } else {
        setResult({ success: false, error: res.error ?? 'Unknown error' });
        onToast(`${action.name} failed`, 'error');
        onExecuted({
          id: crypto.randomUUID(),
          actionId: action.id,
          actionName: action.name,
          timestamp: new Date().toISOString(),
          inputs: { ...values },
          status: 'error',
          error: res.error,
          durationMs,
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setResult({ success: false, error: msg });
      onToast(`${action.name} failed`, 'error');
      onExecuted({
        id: crypto.randomUUID(),
        actionId: action.id,
        actionName: action.name,
        timestamp: new Date().toISOString(),
        inputs: { ...values },
        status: 'error',
        error: msg,
        durationMs: Date.now() - startTime,
      });
    } finally {
      setExecuting(false);
    }
  }, [action, values, onExecuted, onToast]);

  const handleClick = useCallback(() => {
    if (action.dangerLevel === 'destructive') {
      setConfirming(true);
    } else {
      execute();
    }
  }, [action.dangerLevel, execute]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 transition-all hover:shadow-sm">
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center border shrink-0 ${danger.badge}`}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {action.name}
            </span>
            <InfoBadge tip={action.longDescription}>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${danger.badge}`}
              >
                {danger.label}
              </span>
            </InfoBadge>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{action.description}</p>
        </div>
      </div>

      {/* Fields */}
      {action.fields.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          {action.fields.map((field) => (
            <div key={field.name}>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">
                {field.label} {field.required && <span className="text-red-400">*</span>}
              </label>
              {field.type === 'store-select' ? (
                <select
                  value={values[field.name] ?? ''}
                  onChange={(e) => setValue(field.name, e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
                >
                  <option value="">All stores</option>
                  {stores.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name} (#{s.id})
                    </option>
                  ))}
                </select>
              ) : field.type === 'platform-select' ? (
                <select
                  value={values[field.name] ?? ''}
                  onChange={(e) => setValue(field.name, e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
                >
                  <option value="">Select platform...</option>
                  {PLATFORM_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'queue-select' ? (
                <select
                  value={values[field.name] ?? ''}
                  onChange={(e) => setValue(field.name, e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
                >
                  <option value="">Select queue...</option>
                  {QUEUE_OPTIONS.map((q) => (
                    <option key={q.value} value={q.value}>
                      {q.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'select' ? (
                <select
                  value={values[field.name] ?? ''}
                  onChange={(e) => setValue(field.name, e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
                >
                  <option value="">Select...</option>
                  {(field.options ?? []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'date' ? (
                <input
                  type="date"
                  value={values[field.name] ?? ''}
                  onChange={(e) => setValue(field.name, e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
                />
              ) : (
                <input
                  type="text"
                  value={values[field.name] ?? ''}
                  onChange={(e) => setValue(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Confirm dialog for destructive actions */}
      {confirming && (
        <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <span className="text-xs font-medium text-red-700 dark:text-red-400">
              Are you sure?
            </span>
          </div>
          <p className="text-[10px] text-red-600 dark:text-red-400 mb-2">
            {action.longDescription}
          </p>
          <div className="flex gap-2">
            <button
              onClick={execute}
              disabled={executing}
              className="px-3 py-1 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
            >
              {executing && <Loader2 className="w-3 h-3 animate-spin" />}
              Yes, proceed
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="px-3 py-1 text-xs font-medium border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Execute button */}
      {!confirming && (
        <button
          onClick={handleClick}
          disabled={executing || !isValid}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${danger.button}`}
        >
          {executing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Play className="w-3.5 h-3.5" />
          )}
          {executing ? 'Running...' : 'Execute'}
        </button>
      )}

      {/* Result */}
      {result && (
        <div
          className={`mt-3 rounded-lg border p-3 ${result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
        >
          <div className="text-[10px] font-medium mb-1 ${result.success ? 'text-green-700' : 'text-red-700'}">
            {result.success ? 'Success' : 'Error'}
          </div>
          {result.error && (
            <p className="text-[10px] text-red-600 dark:text-red-400">{result.error}</p>
          )}
          {result.data && (
            <pre className="text-[10px] text-gray-700 dark:text-gray-200 overflow-auto max-h-40 mt-1">
              {JSON.stringify(result.data, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
